import { useToast } from 'common/hooks/useToast';
import useTranslation from 'common/hooks/useTranslation';
import { useUpdateFavoriteStatusMutation } from 'common/slices/submissionApi.slice';
import { useEffect, useState } from 'react';
import { useDebounce } from 'usehooks-ts';

export const useFavoriteToggle = (
  initialFavoriteStatusValue,
  submissionSlug,
  { onSuccess, onError } = {},
) => {
  const toast = useToast();
  const { t } = useTranslation('offers\\details');

  const [favoriteStatus, setFavoriteStatus] = useState(
    initialFavoriteStatusValue,
  );
  const debouncedFavoriteStatus = useDebounce(favoriteStatus, 500);

  const [updateFavoriteStatus, { isLoading }] =
    useUpdateFavoriteStatusMutation();

  useEffect(() => {
    if (favoriteStatus !== initialFavoriteStatusValue) {
      setFavoriteStatus(initialFavoriteStatusValue);
    }
  }, [initialFavoriteStatusValue]);

  useEffect(() => {
    if (debouncedFavoriteStatus !== initialFavoriteStatusValue) {
      const isFavorite = debouncedFavoriteStatus ? 'true' : 'false';
      const toggleFavorite = async () => {
        try {
          await updateFavoriteStatus({
            submissionSlug,
            isFavorite,
          }).unwrap();
          debouncedFavoriteStatus
            ? toast.newToast('positive', t('toasts.success-mark-favorite'))
            : toast.newToast('positive', t('toasts.success-unmark-favorite'));
          // Call the success callback
          onSuccess?.();
        } catch (err) {
          setFavoriteStatus(initialFavoriteStatusValue); // revert in case of error
          onError?.(err); // allow error handling externally
          if (err?.status === 403) {
            toast.newToast('negative', t('toasts.forbidden'));
          } else {
            console.error(err);
            toast.newToast('negative', t('toasts.something-wrong'));
          }
        }
      };
      toggleFavorite();
    }
  }, [debouncedFavoriteStatus]);

  return { isLoading, favoriteStatus, setFavoriteStatus };
};
